/**
 * The HUD's contextual "[E]" prompt: which world node or NPC owns the E key
 * (sprint-assembly-1, AD-647), and — `q-dungeon-run-world-prompts-leak` — none of
 * them while the player is inside a dungeon run.
 *
 * Inside a run the predicted position is in the instance frame (plan-local, AD-705),
 * so a world node or NPC near the world origin would "answer" a body that is not in
 * the world at all. AD-705 hides the world's roots; this hides their prompts and keys.
 *
 * Display only: the server still decides range, charges and who may talk.
 */

import { talkableNpc, type NpcAt } from './panels/nearby.js';
import type { StationAt } from './wayfinding.js';

/** How close a gather node must be for the HUD's "[E] Gather" prompt (m). */
export const NODE_GATHER_PROMPT_RANGE_M = 4;

export interface NodeAt {
  id: number;
  resource: string;
  x: number;
  z: number;
  depleted: boolean;
}

export interface PromptNode {
  id: number;
  resource: string;
  distanceM: number;
}

/**
 * How close a placed crafting station must be for the HUD's "Craft at <station>" prompt (m)
 * (q-demo-crafting-clarity). A bench is a large prop the player stops in front of, not a node
 * they stand on, so the reach is a little wider than a node's.
 */
export const STATION_CRAFT_PROMPT_RANGE_M = 5;

export interface PromptStation {
  station: string;
  distanceM: number;
}

export type InteractionPrompt<T extends NpcAt> =
  | { kind: 'npc'; npc: T }
  | { kind: 'node'; node: PromptNode }
  | { kind: 'station'; station: PromptStation }
  | undefined;

/** The nearest non-depleted node within range of `self`, or undefined. */
export function nearestNode(
  self: { x: number; z: number } | null,
  nodes: readonly NodeAt[],
  rangeM: number = NODE_GATHER_PROMPT_RANGE_M,
  canGather?: (resource: string) => boolean,
): PromptNode | undefined {
  if (!self) return undefined;
  let best: PromptNode | undefined;
  let bd = rangeM;
  for (const n of nodes) {
    if (n.depleted) continue;
    if (canGather && !canGather(n.resource)) continue;
    const d = Math.hypot(n.x - self.x, n.z - self.z);
    if (d < bd) {
      bd = d;
      best = { id: n.id, resource: n.resource, distanceM: d };
    }
  }
  return best;
}

/** The nearest placed crafting station within range of `self`, or undefined (range exclusive). */
export function nearestStation(
  self: { x: number; z: number } | null,
  stations: readonly StationAt[],
  rangeM: number = STATION_CRAFT_PROMPT_RANGE_M,
): PromptStation | undefined {
  if (!self) return undefined;
  let best: PromptStation | undefined;
  let bd = rangeM;
  for (const s of stations) {
    const d = Math.hypot(s.x - self.x, s.z - self.z);
    if (d < bd) {
      bd = d;
      best = { station: s.station, distanceM: d };
    }
  }
  return best;
}

/**
 * The prompt the HUD should offer: the nearer of the in-range NPC and node (the NPC only
 * when strictly closer, so a tie gathers), else a crafting station in range, or nothing —
 * always nothing inside a run.
 *
 * q-demo-crafting-clarity (owner, Desk #279: the tester did not know crafting "meant going to a
 * menu"): standing at a bench offers "Craft at <station>", which opens Crafting directly. A
 * node or NPC in reach keeps the E key — they are the smaller, more specific targets, and a
 * bench is only ever beside them, never on top of them.
 */
export function interactionPrompt<T extends NpcAt>(input: {
  self: { x: number; z: number } | null;
  inDungeonRun: boolean;
  nodes: readonly NodeAt[];
  npcs: readonly T[];
  /** Optional filter (the guest demo's rank lock, `guestCanGather`); absent ⇒ every node. */
  canGather?: (resource: string) => boolean;
  /** The zone's placed crafting stations (`stationsIn`); absent ⇒ none. */
  stations?: readonly StationAt[];
}): InteractionPrompt<T> {
  if (input.inDungeonRun) return undefined;
  const node = nearestNode(input.self, input.nodes, NODE_GATHER_PROMPT_RANGE_M, input.canGather);
  const npc = talkableNpc(input.npcs, input.self);
  if (npc && (!node || npc.distanceM < node.distanceM)) return { kind: 'npc', npc: npc.npc };
  if (node) return { kind: 'node', node };
  const station = nearestStation(input.self, input.stations ?? []);
  return station ? { kind: 'station', station } : undefined;
}
